import { PostCoverCard } from "@/components/blog/PostCoverCard";
import { blogPostPath } from "@/constants/routes";
import { Badge, Card, Text } from "@/design-system";
import type { BlogPostSummary } from "@/shared/blog/blogTypes";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Link } from "react-router";

const formatDate = (iso: string | null): string =>
  iso ? format(new Date(iso), "dd/MM/yyyy", { locale: ptBR }) : "";

/**
 * Cartão de post na grade da listagem, do hub de cluster e de "Continue lendo".
 *
 * A capa é `aria-hidden` e fora da ordem de tabulação: ela repete o mesmo
 * destino do título, e dois links idênticos seguidos só rendem repetição para
 * quem navega por teclado ou ouve a página.
 *
 * O `<article>` vive dentro do `Card` porque o `Card` do Design System é
 * sempre uma `div` — a semântica fica no filho, e a superfície continua a do
 * sistema em vez de uma borda montada na mão.
 */
export const PostCard = ({ post }: { post: BlogPostSummary }) => (
  <Card padding="compact" className="hover:border-ds-brand-500 h-full transition-colors">
    <article className="flex h-full flex-col">
      <Link to={blogPostPath(post.slug)} tabIndex={-1} aria-hidden="true" className="block">
        <PostCoverCard publishedAt={post.publishedAt} />
      </Link>

      <div className="flex flex-1 flex-col gap-3 pt-4">
        {post.category && (
          // Categoria composta ("Gestão Financeira / Recuperação de Crédito")
          // passava da largura do card: o Badge é `whitespace-nowrap`. Aqui
          // ela quebra em linha em vez de vazar — cortar com reticências
          // esconderia justamente a parte que diferencia a categoria.
          <Badge
            tone="brand"
            shape="pill"
            size="sm"
            className="w-fit max-w-full text-left whitespace-normal"
          >
            {post.category}
          </Badge>
        )}

        <h3 className="font-ds-sans text-ds-h4 text-ds-ink-900 text-pretty">
          <Link
            to={blogPostPath(post.slug)}
            className="rounded-ds-sm hover:text-ds-brand-700 focus-visible:ring-ds-focus ease-ds-out cursor-pointer transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none"
          >
            {post.title}
          </Link>
        </h3>

        {/* Sem o resumo, o card de título curto ficava com um vão até o
            autor — a grade estica os três cards à altura do mais alto. */}
        {post.excerpt && (
          <Text size="small" tone="muted" className="line-clamp-3">
            {post.excerpt}
          </Text>
        )}

        <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-2">
          {post.authorName && (
            <Text as="span" size="caption" tone="subtle">
              Por {post.authorName}
            </Text>
          )}
          {post.publishedAt && (
            <Text as="span" size="caption" tone="subtle" numeric>
              <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
            </Text>
          )}
        </div>
      </div>
    </article>
  </Card>
);
